import { Injectable, NgZone } from '@angular/core';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { applyTransaction, logAction } from '@datorama/akita';
import {
  Observable,
  Subscription,
  throwError,
  catchError,
  map,
  tap,
  distinctUntilChanged,
} from 'rxjs';
import { SearchVariantsApiService } from '../services/search-variants.service';
import { AkitaFeaturedStore } from './featured.store';
import { FeaturedReviewModel } from '@app/shared/models/api/featured-review.model';
import { AkitaFeaturedState } from '../models/featured.state';
import { AkitaFeaturedQuery } from './featured.query';

@Injectable({ providedIn: 'root' })
export class AkitaFeaturedService {
  constructor(
    private readonly zone: NgZone,
    private readonly store: AkitaFeaturedStore,
    private readonly query: AkitaFeaturedQuery,
    private readonly searchVariantsApiService: SearchVariantsApiService
  ) {}

  public findFeaturedReviewsAsync(
    language?: string | null,
    model?: string | null
  ): Subscription {
    return this.findFeaturedReviews(language, model).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public findFeaturedReviews(
    language?: string | null,
    model?: string | null
  ): Observable<Array<FeaturedReviewModel>> {
    if (!model) {
      return throwError(() =>
        parseApiError({
          code: 'REQUIRED_INFO_MISSING',
          message: 'The properties "model" is required',
        })
      );
    } else if (!language) {
      return throwError(() =>
        parseApiError({
          code: 'REQUIRED_INFO_MISSING',
          message: 'The properties "language" is required',
        })
      );
    } else {
      // CHECK FOR EXISTING Reviews on the state
      if (
        (this.query.getValue().featuredReviews &&
          this.query.getValue().featuredReviews[language] &&
          this.query.getValue().featuredReviews[language][model]) ||
        this.query.getValue().fetchingFeaturedReviews[language]
      ) {
        return this.query.select().pipe(
          map((state: AkitaFeaturedState) => state.featuredReviews[language][model]),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        );
      }

      this.zone.run(() => {
        applyTransaction(() => {
          logAction('findFeaturedReviews()');
          this.store.setErrorFetchingFeaturedReviews(language, null);
          this.store.toggleFetchingFeaturedReviews(language, true);
        });
      });

      return this.searchVariantsApiService.fetchFeaturedReviews(language, model).pipe(
        catchError((error: unknown) => {
          const parsed = parseApiError(error);
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('findAllReviews() - error');
              this.store.setErrorFetchingFeaturedReviews(language, parsed);
              this.store.toggleFetchingFeaturedReviews(language, false);
            });
          });
          return throwError(() => parsed);
        }),
        map((response: Array<FeaturedReviewModel>) => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('findAllReviews() - done');
              this.store.setAllFeaturedReviews(language, response);
              this.store.toggleFetchingFeaturedReviews(language, false);
            });
          });

          return response.filter((prod) => prod.model.indexOf(model) !== -1);
        })
      );
    }
  }

  public findAllReviewsAsync(language?: string | null, limit?: number): Subscription {
    return this.findAllReviews(language, limit).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public findAllReviews(
    language?: string | null,
    limit?: number
  ): Observable<Array<FeaturedReviewModel>> {
    // CHECK FOR EXISTING Reviews on the state
    if (
      language &&
      ((this.query.getValue().featuredReviews &&
        this.query.getValue().featuredReviews[language]) ||
        this.query.getValue().fetchingFeaturedReviews[language])
    ) {
      return this.query.select().pipe(
        map((state: AkitaFeaturedState) => state.featuredReviews[language]['all']),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('findAllReviews()');
        this.store.setErrorFetchingFeaturedReviews(language, null);
        this.store.toggleFetchingFeaturedReviews(language, true);
      });
    });

    return this.searchVariantsApiService.fetchFeaturedReviews(language, null, limit).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('findAllReviews() - error');
            this.store.setErrorFetchingFeaturedReviews(language, parsed);
            this.store.toggleFetchingFeaturedReviews(language, false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: Array<FeaturedReviewModel>) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('findAllReviews() - done');
            this.store.setAllFeaturedReviews(language, response);
            this.store.toggleFetchingFeaturedReviews(language, false);
          });
        });
      })
    );
  }
}
